import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangePasswordModal, ChangeTenantModal, HeaderNovedadesMenu, UserMenu } from '@/presentation/components/common';
import { SidebarAssistantChat } from '@/presentation/components/assistant/SidebarAssistantChat';
import { useAuth } from '@/presentation/contexts/AuthContext';

const NAV_ICONS: Record<string, React.ReactNode> = {
  dashboard: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-18v6h8V3h-8z" />
    </svg>
  ),
  tenants: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  plans: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  adicionales: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  ),
  costProjection: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  ),
  collections: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  clinicInvoices: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  ecosystemUsage: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  reminderAnalytics: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  billing: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  billingHistory: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  reports: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  periodOps: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  periodSettings: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  support: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  documentation: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  emails: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  emailSettings: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  solicitudes: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  settings: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  appSettings: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  ),
  expenseCategories: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m-6 4h6m-6 4h3m-7 5h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  users: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  coofitConexion: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
      />
    </svg>
  ),
  comercial: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  ),
  auditoria: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  ),
  logs: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
};

const GROUP_ICONS: Record<MenuGroupKey, React.ReactNode> = {
  clinics: NAV_ICONS.tenants,
  products: NAV_ICONS.adicionales,
  finance: NAV_ICONS.billing,
  support: NAV_ICONS.support,
  communication: NAV_ICONS.emails,
  system: NAV_ICONS.settings,
};

type MenuGroupKey =
  | 'clinics'
  | 'products'
  | 'finance'
  | 'support'
  | 'communication'
  | 'system';

type MenuItem = {
  key: string;
  label: string;
  path: string;
};

type MenuGroup = {
  key: MenuGroupKey;
  title: string;
  items: MenuItem[];
};

type FloatingMenuPosition = {
  top: number;
};

const MENU_GROUP_STORAGE_KEY = 'coofit.sidebar.menuGroups';

const DEFAULT_GROUP_STATE: Record<MenuGroupKey, boolean> = {
  clinics: false,
  products: false,
  finance: false,
  support: false,
  communication: false,
  system: false,
};

const readStoredGroupState = (): Record<MenuGroupKey, boolean> => {
  if (typeof window === 'undefined') {
    return DEFAULT_GROUP_STATE;
  }

  try {
    const stored = window.localStorage.getItem(MENU_GROUP_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_GROUP_STATE;
    }

    const parsed = JSON.parse(stored) as Partial<Record<MenuGroupKey, boolean>>;
    return {
      ...DEFAULT_GROUP_STATE,
      ...Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => typeof value === 'boolean')
      ),
    } as Record<MenuGroupKey, boolean>;
  } catch {
    return DEFAULT_GROUP_STATE;
  }
};

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SearchIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const ClearIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const areGroupStatesEqual = (
  first: Record<MenuGroupKey, boolean>,
  second: Record<MenuGroupKey, boolean>,
) => Object.keys(DEFAULT_GROUP_STATE).every((key) => {
  const groupKey = key as MenuGroupKey;
  return first[groupKey] === second[groupKey];
});

const renderHighlightedLabel = (label: string, searchTerm: string) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) {
    return label;
  }

  const lowerLabel = label.toLowerCase();
  const startIndex = lowerLabel.indexOf(normalizedSearch);
  if (startIndex === -1) {
    return label;
  }

  const endIndex = startIndex + normalizedSearch.length;

  return (
    <>
      {label.slice(0, startIndex)}
      <mark className="rounded bg-yellow-200 px-0.5 font-semibold text-slate-950">
        {label.slice(startIndex, endIndex)}
      </mark>
      {label.slice(endIndex)}
    </>
  );
};

export const MainLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, canAdminister, canOperateBilling, canManageUsers, mustChangePassword } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [showChangeTenantModal, setShowChangeTenantModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openCollapsedGroup, setOpenCollapsedGroup] = useState<MenuGroupKey | null>(null);
  const [floatingMenuPosition, setFloatingMenuPosition] = useState<FloatingMenuPosition | null>(null);
  const [closeMenuTimeout, setCloseMenuTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<MenuGroupKey, boolean>>(
    readStoredGroupState
  );

  useEffect(() => {
    window.localStorage.setItem(MENU_GROUP_STORAGE_KEY, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  useEffect(() => () => {
    if (closeMenuTimeout) {
      clearTimeout(closeMenuTimeout);
    }
  }, [closeMenuTimeout]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const dashboardItem: MenuItem = useMemo(
    () => ({ key: 'dashboard', label: t('menu.dashboard'), path: '/dashboard' }),
    [t],
  );

  const menuGroups: MenuGroup[] = useMemo(() => [
    {
      key: 'clinics',
      title: t('menu.groups.clinics'),
      items: [
        { key: 'tenants', label: t('menu.tenants'), path: '/tenants' },
      ],
    },
    {
      key: 'products',
      title: t('menu.groups.products'),
      items: [
        { key: 'plans', label: t('menu.plans'), path: '/plans' },
        {
          key: 'comercial',
          label: t('menu.comercial'),
          path: '/comercial',
        },
        { key: 'adicionales', label: t('menu.adicionales'), path: '/adicionales' },
        { key: 'ecosystemUsage', label: t('menu.ecosystemUsage'), path: '/ecosystem-usage' },
      ],
    },
    {
      key: 'finance',
      title: t('menu.groups.finance'),
      items: [
        { key: 'billing', label: t('menu.billing'), path: '/panel-facturacion' },
        { key: 'billingHistory', label: t('menu.billingHistory'), path: '/panel-facturacion-historico' },
        { key: 'reports', label: t('menu.reports'), path: '/reports' },
        { key: 'collections', label: t('menu.collections'), path: '/collections' },
        { key: 'clinicInvoices', label: t('menu.clinicInvoices'), path: '/facturas-clinicas' },
        { key: 'costProjection', label: t('menu.costProjection'), path: '/proyeccion-costos' },
        ...(canAdminister
          ? [{ key: 'periodOps', label: t('menu.periodOps'), path: '/period-ops' }]
          : []),
      ],
    },
    {
      key: 'support',
      title: t('menu.groups.support'),
      items: [
        { key: 'support', label: t('menu.support'), path: '/support' },
        { key: 'solicitudes', label: t('menu.solicitudes'), path: '/solicitudes' },
        { key: 'documentation', label: t('menu.documentation'), path: '/documentation' },
      ],
    },
    {
      key: 'communication',
      title: t('menu.groups.communication'),
      items: [
        { key: 'emails', label: t('menu.emails'), path: '/emails' },
        {
          key: 'reminderAnalytics',
          label: t('menu.reminderAnalytics'),
          path: '/reminder-analytics',
        },
      ],
    },
    {
      key: 'system',
      title: t('menu.groups.system'),
      items: [
        {
          key: 'appSettings',
          label: t('menu.appSettings'),
          path: '/configuracion-app',
        },
        ...(canAdminister
          ? [{ key: 'periodSettings', label: t('menu.periodSettings'), path: '/configuracion-periodo' }]
          : []),
        {
          key: 'settings',
          label: t('menu.whatsappSettings'),
          path: '/configuracion-whatsapp',
        },
        {
          key: 'emailSettings',
          label: t('menu.emailSettings'),
          path: '/configuracion-email',
        },
        {
          key: 'expenseCategories',
          label: t('menu.expenseCategories'),
          path: '/categorias-gastos',
        },
        ...(canManageUsers
          ? [{ key: 'users', label: t('menu.users'), path: '/configuracion-usuarios' }]
          : []),
        ...(canAdminister || canOperateBilling
          ? [
              { key: 'auditoria', label: t('menu.auditoria'), path: '/auditorias' },
              { key: 'logs', label: t('menu.logs'), path: '/logs' },
            ]
          : []),
      ],
    },
  ], [canAdminister, canOperateBilling, canManageUsers, t]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const filteredMenuGroups = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return menuGroups;
    }

    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(normalizedSearch)),
      }))
      .filter((group) => group.items.length > 0);
  }, [menuGroups, searchTerm]);

  useEffect(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return;
    }

    const nextState = { ...DEFAULT_GROUP_STATE };
    menuGroups.forEach((group) => {
      nextState[group.key] = group.items.some((item) =>
        item.label.toLowerCase().includes(normalizedSearch)
      );
    });

    setExpandedGroups((current) => (areGroupStatesEqual(current, nextState) ? current : nextState));
  }, [menuGroups, searchTerm]);

  const navLinkClass = (path: string, collapsed: boolean, floating = false) =>
    `group flex min-h-10 items-center gap-3 rounded-md text-[13px] font-semibold transition-all duration-150 ${
      floating
        ? 'px-4 py-2'
        : collapsed
          ? 'justify-center px-2 py-2.5'
          : 'px-3 py-2.5 md:py-2.5'
    } ${
      isActive(path)
        ? 'bg-teal-500/15 text-teal-300 shadow-sm'
        : 'text-slate-300 hover:bg-teal-500/10 hover:text-white'
    }`;

  const toggleGroup = (groupKey: MenuGroupKey) => {
    setExpandedGroups((current) => {
      const nextState = { ...DEFAULT_GROUP_STATE };
      nextState[groupKey] = !current[groupKey];
      return nextState;
    });
  };

  const clearCollapsedMenuTimeout = () => {
    if (closeMenuTimeout) {
      clearTimeout(closeMenuTimeout);
      setCloseMenuTimeout(null);
    }
  };

  const scheduleCollapsedMenuClose = () => {
    clearCollapsedMenuTimeout();
    const timeout = setTimeout(() => {
      setOpenCollapsedGroup(null);
      setFloatingMenuPosition(null);
    }, 200);
    setCloseMenuTimeout(timeout);
  };

  const closeCollapsedMenu = () => {
    clearCollapsedMenuTimeout();
    setOpenCollapsedGroup(null);
    setFloatingMenuPosition(null);
  };

  const renderSearch = () => (
    <div className="px-4 pb-1.5 pt-4">
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
          <SearchIcon />
        </div>
        <input
          type="text"
          placeholder={`${t('common.search')}...`}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="block w-full rounded-md border border-slate-700 bg-slate-900/60 py-2 pl-10 pr-9 text-[13px] font-medium leading-5 text-slate-100 placeholder:text-slate-400 outline-none transition focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 transition hover:text-slate-200"
            aria-label={t('common.clear') || 'Limpiar busqueda'}
            title={t('common.clear') || 'Limpiar busqueda'}
          >
            <ClearIcon />
          </button>
        )}
      </div>
    </div>
  );

  const renderMenuItem = (
    item: MenuItem,
    collapsed: boolean,
    options?: { onNavigate?: () => void; floating?: boolean },
  ) => (
    <Link
      key={item.key}
      to={item.path}
      title={collapsed ? item.label : undefined}
      onClick={() => {
        closeCollapsedMenu();
        options?.onNavigate?.();
      }}
      className={navLinkClass(item.path, collapsed, options?.floating)}
    >
      <span className="flex-shrink-0 opacity-90">{NAV_ICONS[item.key]}</span>
      {!collapsed && (
        <span className="truncate">
          {renderHighlightedLabel(item.label, searchTerm)}
        </span>
      )}
    </Link>
  );

  const renderNav = (collapsed: boolean, onNavigate?: () => void) => (
    <nav className={`flex-1 ${collapsed ? 'px-2 py-4' : 'px-3 pb-3'} space-y-3`}>
      {!collapsed && renderSearch()}
      <div className="space-y-1">
        <Link
          to={dashboardItem.path}
          title={collapsed ? dashboardItem.label : undefined}
          onClick={() => {
            closeCollapsedMenu();
            onNavigate?.();
          }}
          className={navLinkClass(dashboardItem.path, collapsed)}
        >
          <span className="flex-shrink-0 opacity-90">{NAV_ICONS[dashboardItem.key]}</span>
          {!collapsed && <span className="truncate">{dashboardItem.label}</span>}
        </Link>
      </div>

      {collapsed ? (
        <div
          className="space-y-2"
          onMouseEnter={clearCollapsedMenuTimeout}
          onMouseLeave={scheduleCollapsedMenuClose}
        >
          {menuGroups.map((group) => {
            const isGroupActive = group.items.some((item) => isActive(item.path));
            const isOpen = openCollapsedGroup === group.key;

            return (
              <button
                key={group.key}
                type="button"
                title={group.title}
                aria-label={group.title}
                onMouseEnter={(event) => {
                  clearCollapsedMenuTimeout();
                  const buttonRect = event.currentTarget.getBoundingClientRect();
                  setFloatingMenuPosition({ top: buttonRect.top });
                  setOpenCollapsedGroup(group.key);
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                className={`flex min-h-10 w-full items-center justify-center rounded-md px-2 py-2 text-sm font-semibold transition-all duration-150 ${
                  isGroupActive
                    ? 'bg-teal-500/15 text-teal-300 shadow-sm'
                    : isOpen
                      ? 'bg-teal-500/10 text-teal-300'
                      : 'text-slate-500 hover:bg-teal-500/10 hover:text-teal-300'
                }`}
              >
                <span className="flex-shrink-0">{GROUP_ICONS[group.key]}</span>
              </button>
            );
          })}
        </div>
      ) : (searchTerm.trim() ? filteredMenuGroups : menuGroups).map((group) => (
        <div key={group.key}>
          <button
            type="button"
            onClick={() => toggleGroup(group.key)}
            className="mb-1 flex w-full items-center justify-between rounded-md px-3 py-1 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-teal-300 transition-colors hover:bg-teal-500/10 focus:outline-none focus:ring-2 focus:ring-teal-500/70"
            aria-expanded={expandedGroups[group.key]}
            aria-controls={`sidebar-group-${group.key}`}
            title={expandedGroups[group.key] ? 'Colapsar seccion' : 'Expandir seccion'}
          >
            <span className="truncate">{group.title}</span>
            <ChevronIcon expanded={expandedGroups[group.key]} />
          </button>
          <div
            id={`sidebar-group-${group.key}`}
            className={`overflow-hidden transition-all duration-200 ease-in-out ${
              expandedGroups[group.key] ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="space-y-1">
              {group.items.map((item) => renderMenuItem(item, false, { onNavigate }))}
            </div>
          </div>
        </div>
      ))}
    </nav>
  );

  const renderCollapsedFloatingMenu = () => {
    if (!desktopCollapsed || !openCollapsedGroup || !floatingMenuPosition) {
      return null;
    }

    const group = menuGroups.find((candidate) => candidate.key === openCollapsedGroup);
    if (!group) {
      return null;
    }

    return (
      <div
        className="fixed left-16 z-50 max-h-[80vh] w-56 overflow-y-auto rounded-md border border-slate-700 bg-slate-900 py-2 shadow-2xl shadow-slate-950/40"
        style={{ top: floatingMenuPosition.top, marginLeft: '0.5rem' }}
        onMouseEnter={clearCollapsedMenuTimeout}
        onMouseLeave={scheduleCollapsedMenuClose}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-700 px-4 py-2">
          <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-slate-300">
            {group.title}
          </h3>
        </div>
        <div className="py-1">
          {group.items.map((item) => renderMenuItem(item, false, { floating: true }))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen max-w-[100vw] flex-col overflow-hidden bg-slate-100 dark:bg-gray-950">
      {/* Banner full-bleed (por encima del sidebar) */}
      <header className="relative z-40 flex-shrink-0 border-b border-slate-200/70 bg-white/90 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/90 supports-[backdrop-filter]:bg-white/75 dark:supports-[backdrop-filter]:bg-gray-900/75">
        <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 sm:gap-3 sm:px-4 sm:py-2 md:px-5 md:py-2 lg:px-6">
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2.5 md:gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="panel-touch md:hidden rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800"
              aria-label={t('layout.openMenu')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setDesktopCollapsed((v) => !v)}
              className="panel-touch hidden md:inline-flex rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle sidebar"
              title={desktopCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 text-sm font-bold tracking-tight text-white shadow-sm shadow-teal-900/20">
                C
              </span>
              <div className="min-w-0">
                <span className="block truncate text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100">
                  Coofit
                </span>
                <span className="hidden text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500 sm:block">
                  Panel
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
            <HeaderNovedadesMenu />
            <button
              type="button"
              onClick={() => setShowChangeTenantModal(true)}
              className="inline-flex h-10 min-h-11 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700 sm:px-3 sm:text-sm md:h-10"
              title={t('pages.tenants.changeTenant')}
              aria-label={t('pages.tenants.changeTenant')}
            >
              <svg className="h-4 w-4 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span className="hidden max-w-[9rem] truncate sm:inline md:max-w-[11rem]">{t('pages.tenants.changeTenant')}</span>
            </button>
            {user && (
              <UserMenu
                username={user.username}
                onLogout={handleLogout}
                onChangePassword={() => setShowChangePasswordModal(true)}
              />
            )}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* Sidebar tablet+ (md iPad portrait rail; lg landscape más ancho) */}
        <aside
          className={`relative z-30 hidden md:flex flex-col flex-shrink-0 border-r border-slate-800 bg-slate-900 transition-[width] duration-300 ease-in-out overflow-visible ${
            desktopCollapsed ? 'w-16' : 'w-[196px] lg:w-[200px]'
          }`}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            {renderNav(desktopCollapsed)}
          </div>
          <SidebarAssistantChat collapsed={desktopCollapsed} panelSide="right" />
        </aside>

        {renderCollapsedFloatingMenu()}

        {/* Sidebar mobile overlay */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-slate-950/50 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed inset-y-0 left-0 z-50 flex w-[min(18rem,88vw)] flex-col overflow-visible bg-slate-950 shadow-2xl md:hidden">
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-800/80 px-4 py-3.5">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 text-sm font-bold text-white">
                    C
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">Coofit</p>
                    <p className="truncate text-[11px] text-slate-400">
                      {user?.username || t('layout.currentUser')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="panel-touch rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white"
                  aria-label={t('common.close')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden panel-safe-bottom">
                {renderNav(false, () => setSidebarOpen(false))}
              </div>
              <SidebarAssistantChat collapsed={false} panelSide="inside" />
            </aside>
          </>
        )}

        <main
          className={`min-h-0 min-w-0 flex-1 bg-slate-100 dark:bg-gray-950 ${
            location.pathname.includes('configuracion') ||
            location.pathname.startsWith('/comercial')
              ? 'flex flex-col overflow-hidden'
              : 'overflow-auto overscroll-y-contain'
          }`}
        >
          <Outlet />
        </main>
      </div>

      <ChangeTenantModal
        isOpen={showChangeTenantModal}
        onClose={() => setShowChangeTenantModal(false)}
      />
      <ChangePasswordModal
        isOpen={mustChangePassword}
        mode="forced"
      />
      <ChangePasswordModal
        isOpen={!mustChangePassword && showChangePasswordModal}
        mode="profile"
        onClose={() => setShowChangePasswordModal(false)}
      />
    </div>
  );
};
